import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, Stack, TextField,
} from '@mui/material';
import { SampleRequestService } from '../../services/sampleRequest.service';
import { AuthService } from '../../services/auth.service';
import { ISampleRequest, SampleStatus } from '../../interfaces/garments/sampleRequest.interface';
import { legalStatusTargets } from './sampleRequestTransitions';
import { useToast } from '../../context/ToastContext';
import { enumLabel } from '../../utils/garmentsLabels';

interface Props {
    open: boolean;
    sampleRequest: ISampleRequest;
    onClose: () => void;
    onUpdated: (updated: ISampleRequest) => void;
}

/**
 * Operational status move (PATCH `{id}/status`) — offers only `legalStatusTargets(currentStatus)`,
 * mirroring `SampleRequestService.STATUS_TRANSITIONS` on the backend (REQUESTED/IN_WORK forward).
 * `RESUBMITTED` never appears here — that value is reachable only through the Resubmit action.
 */
export function SampleStatusDialog({ open, sampleRequest, onClose, onUpdated }: Props) {
    const toast = useToast();
    const options = legalStatusTargets(sampleRequest.status);

    const [target, setTarget] = useState<SampleStatus | ''>('');
    const [courierName, setCourierName] = useState('');
    const [courierAwb, setCourierAwb] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTarget(options[0] ?? '');
            setCourierName('');
            setCourierAwb('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sampleRequest.id]);

    const needsCourier = target === 'SENT';

    const saveWhy = saving ? null
        : !target ? 'Pick the new status.'
        : null;

    const handleSubmit = async () => {
        if (!target) return;
        if (needsCourier && (!courierName.trim() || !courierAwb.trim())) {
            toast.showError('Moving to SENT requires a courier name and AWB number');
            return;
        }
        const institutionId = AuthService.getSelectedInstitution();
        setSaving(true);
        try {
            const updated = await SampleRequestService.updateStatus(institutionId, sampleRequest.id, {
                status: target,
                courierName: needsCourier ? courierName.trim() : null,
                courierAwb: needsCourier ? courierAwb.trim() : null,
            });
            toast.showSuccess(`Status changed to ${enumLabel(target)}`);
            onUpdated(updated);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to change status');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'merchandising.sampleStatus.page' } as any}>
            <DialogTitle>Change Status</DialogTitle>
            <DialogContent data-help-section="merchandising.sampleStatus.section.status">
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl data-help="merchandising.sampleStatus.field.target" fullWidth size="small" disabled={saving || options.length === 0}>
                        <InputLabel id="sample-status-dialog-target-label">New Status</InputLabel>
                        <Select
                            labelId="sample-status-dialog-target-label"
                            label="New Status"
                            value={target}
                            onChange={e => setTarget(e.target.value as SampleStatus)}
                        >
                            {options.map(status => (
                                <MenuItem key={status} value={status}>{enumLabel(status)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {needsCourier && (
                        <>
                            <TextField
                                data-help="merchandising.sampleStatus.field.courierName"
                                label="Courier Name" size="small" fullWidth required disabled={saving}
                                value={courierName} onChange={e => setCourierName(e.target.value)}
                                inputProps={{ maxLength: 120 }}
                            />
                            <TextField
                                data-help="merchandising.sampleStatus.field.courierAwb"
                                label="Courier AWB" size="small" fullWidth required disabled={saving}
                                value={courierAwb} onChange={e => setCourierAwb(e.target.value)}
                                inputProps={{ maxLength: 120 }}
                            />
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="merchandising.sampleStatus.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" data-help="merchandising.sampleStatus.submit" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} onClick={handleSubmit} disabled={saving || !target}>
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default SampleStatusDialog;
